import { 
  signIn, 
  signInWithRedirect,
  fetchAuthSession, 
  confirmSignIn, 
  getCurrentUser, 
  fetchUserAttributes,
  signOut 
} from "@aws-amplify/auth";
import logger from "../../../utils/secureLogger";
import {
  AUTH_CONFIG,
  AUTH_ERROR_MESSAGES,
  getUserHighestRole,
} from "../config/authConfig";
import { authConfig } from "@/config/auth-config";

/**
 * Authentication Service with Okta SAML Integration
 * Follows SOLID principles for maintainable authentication
 */
class AuthService {
  constructor() {
    this.initializeService();
  }

  initializeService() {
    logger.debug("🔧 AuthService initialized with Okta SAML support");
    logger.debug("🏢 SAML Provider:", authConfig.samlProvider);
    logger.debug("🌐 Cognito Domain:", authConfig.cognitoDomain);
  }

  // ========== OKTA SAML METHODS ==========

  /**
   * Clear expired tokens and reset authentication state
   */
  async clearExpiredTokens() {
    try {
      logger.debug("🧹 Clearing potentially expired tokens...");
      // Clear localStorage tokens
      localStorage.removeItem('amplify-signin-with-hostedUI');
      localStorage.removeItem('amplify-redirected-from-hosted-ui');
      
      // Clear any other Amplify auth tokens
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('CognitoIdentityServiceProvider.')) {
          localStorage.removeItem(key);
        }
      });
      
      logger.debug("✅ Expired tokens cleared");
    } catch (error) {
      logger.debug("❌ Error clearing tokens:", error);
    }
  }

  /**
   * Check if user is authenticated without throwing errors
   * @returns {Promise<boolean>}
   */
  async isAuthenticated() {
    try {
      logger.debug("🔍 Checking authentication status...");
      const user = await getCurrentUser();
      logger.debug("✅ User authenticated:", user?.username || 'unknown');
      return !!user;
    } catch (error) {
      logger.debug("❌ Authentication check failed:", error.name, error.message);
      
      // If it's a token-related error, clear expired tokens
      if (error.name === 'NotAuthorizedException' || 
          error.message.includes('token') || 
          error.message.includes('expired')) {
        await this.clearExpiredTokens();
      }
      
      return false;
    }
  }

  /**
   * Initiate Okta SAML login through Cognito Hosted UI
   * @returns {Promise<{alreadyAuthenticated: boolean}>}
   */
  async signInWithOkta() {
    try {
      // Check if user is already authenticated (without throwing errors)
      const isAuth = await this.isAuthenticated();
      if (isAuth) {
        logger.debug("✅ User already authenticated, skipping redirect");
        return { alreadyAuthenticated: true };
      }

      logger.debug("🔐 Initiating Okta SAML login...");
      logger.debug("🔧 Using SAML provider:", authConfig.samlProvider);

      await signInWithRedirect({
        provider: {
          custom: authConfig.samlProvider, // 'LLA-CES-OKTA'
        },
      });
      
      return { alreadyAuthenticated: false };
    } catch (error) {
      // Handle specific case of already authenticated user
      if (error.name === 'UserAlreadyAuthenticatedException') {
        logger.debug("✅ User already authenticated, skipping login");
        return { alreadyAuthenticated: true };
      }
      
      logger.error("❌ Okta SAML login error:", error);
      throw new Error(`Okta login failed: ${error.message}`);
    }
  }

  /**
   * Check if Okta integration is enabled
   * @returns {boolean}
   */
  isOktaEnabled() {
    return authConfig.okta?.enabled === true;
  }

  /**
   * Get Okta configuration for UI
   * @returns {Object}
   */
  getOktaConfig() {
    return authConfig.okta || {};
  }

  // ========== DIRECT LOGIN METHODS ==========

  async signIn(credentials) {
    try {
      this.validateCredentials(credentials);
      logger.debug("🔑 Attempting direct login for:", credentials.username);
      
      return await signIn({
        username: credentials.username,
        password: credentials.password,
      });
    } catch (error) {
      logger.error("❌ Direct login error:", error);
      throw new Error('Authentication failed');
    }
  }

  async confirmSignIn(newPassword) {
    try {
      if (!newPassword || newPassword.length < AUTH_CONFIG.VALIDATION.MIN_PASSWORD_LENGTH) {
        throw new Error(
          `Password must be at least ${AUTH_CONFIG.VALIDATION.MIN_PASSWORD_LENGTH} characters long`
        );
      }

      return await confirmSignIn({ challengeResponse: newPassword });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  // ========== SESSION MANAGEMENT ==========

  async fetchSession(forceRefresh = true) {
    try {
      return await fetchAuthSession({ forceRefresh });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  async getCurrentUser() {
    try {
      return await getCurrentUser();
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  async getUserAttributes() {
    try {
      return await fetchUserAttributes();
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get complete user data after successful authentication (Okta or Direct)
   * @returns {Promise<Object>}
   */
  async getCompleteUserData() {
    try {
      logger.debug("🔄 Getting complete user data...");
      
      // Wait longer for Okta callback to complete
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Try to get session with force refresh
      const session = await this.fetchSession(true);

      if (!session.tokens || !session.tokens.accessToken) {
        logger.debug("❌ No valid tokens in session");
        throw new Error("No authentication tokens received");
      }

      logger.debug("✅ Valid session found, getting user info...");

      // Get user info from session tokens instead of API calls
      const userData = await this.getUserDataFromTokens(session);

      logger.debug("✅ Complete user data retrieved:");
      logger.debug("👤 Username:", userData.username);
      logger.debug("📧 Email:", userData.email);
      logger.debug("👥 Groups:", userData.groups);

      return userData;
    } catch (error) {
      logger.error("❌ Error getting user data:", error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Extract user data directly from JWT tokens to avoid API calls
   * @param {Object} session - Auth session with tokens
   * @returns {Object} User data
   */
  async getUserDataFromTokens(session) {
    try {
      const { idToken, accessToken } = session.tokens;
      
      if (!idToken) {
        throw new Error("No ID token available");
      }

      const idTokenPayload = idToken.payload;
      const groups = this.extractUserGroups(session);

      // Try to get current user, but don't fail if it doesn't work
      let username = idTokenPayload.email || idTokenPayload['cognito:username'] || idTokenPayload.sub;
      
      try {
        const user = await this.getCurrentUser();
        username = user.username || username;
      } catch (userError) {
        logger.debug("⚠️ Could not get current user, using token data:", userError.message);
      }

      const userData = {
        username: username,
        email: idTokenPayload.email || username,
        attributes: {
          email: idTokenPayload.email,
          given_name: idTokenPayload.given_name,
          family_name: idTokenPayload.family_name,
          name: idTokenPayload.name,
          'custom:Group': idTokenPayload['custom:Group']
        },
        groups,
        session,
        sub: idTokenPayload.sub
      };

      return userData;
    } catch (error) {
      logger.error("❌ Error extracting user data from tokens:", error);
      throw error;
    }
  }

  extractUserGroups(session) {
    const { idToken, accessToken } = session.tokens || {};
    const groupsFromIdToken = idToken?.payload?.["cognito:groups"];
    const groupsFromAccessToken = accessToken?.payload?.["cognito:groups"];

    const groups = groupsFromIdToken || groupsFromAccessToken || [AUTH_CONFIG.USER_GROUPS.DEFAULT];
    
    logger.debug("🏷️ Extracted groups:", groups);
    return groups;
  }

  /**
   * Sign out user (works for both Okta and direct login)
   * @returns {Promise<void>}
   */
  async signOut() {
    try {
      logger.debug("🚪 Initiating sign out process...");
      
      // Always do local logout only to avoid Okta redirect issues
      logger.debug("🔧 Performing local logout...");
      await signOut();
      
      // Clear all local auth data
      await this.clearAllAuthData();
      
      logger.debug("✅ User signed out successfully");
    } catch (error) {
      logger.error("❌ Sign out error:", error);
      
      // Force clear local data even if remote logout fails
      await this.clearAllAuthData();
      
      // Don't throw error to prevent UI issues
      logger.debug("⚠️ Forced local logout due to error");
    }
  }

  /**
   * Sign out with Okta SAML (includes Okta session termination)
   * NOTE: Currently disabled due to redirect issues
   */
  async signOutWithOkta() {
    try {
      // Use standard logout instead of global to avoid redirect issues
      await signOut();
      logger.debug("🏢 Okta local logout completed");
    } catch (error) {
      logger.error("❌ Okta logout error:", error);
      // Fallback to standard logout
      await signOut();
    }
  }

  /**
   * Check if user authenticated via Okta SAML
   * NOTE: Currently not used due to logout redirect issues
   */
  isUserFromOkta(session) {
    try {
      const idToken = session?.tokens?.idToken;
      if (!idToken) return false;
      
      const payload = idToken.payload;
      const identities = payload.identities;
      
      // Check if user has SAML identity
      if (identities && Array.isArray(identities)) {
        return identities.some(identity => 
          identity.providerName === authConfig.samlProvider ||
          identity.providerType === 'SAML'
        );
      }
      
      // Alternative check: look for SAML-specific claims
      return payload.iss && payload.iss.includes('cognito') && 
             payload['custom:Group'] && 
             payload.groups && payload.groups.includes(authConfig.samlProvider);
    } catch (error) {
      logger.debug("⚠️ Could not determine auth method, assuming Okta:", error);
      return true; // Default to Okta logout for safety
    }
  }

  /**
   * Clear all authentication data (local cleanup)
   */
  async clearAllAuthData() {
    try {
      logger.debug("🧹 Clearing all authentication data...");
      
      // Clear localStorage tokens
      localStorage.removeItem('amplify-signin-with-hostedUI');
      localStorage.removeItem('amplify-redirected-from-hosted-ui');
      
      // Clear all Cognito tokens
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('CognitoIdentityServiceProvider.') ||
            key.startsWith('amplify-') ||
            key.includes('idToken') ||
            key.includes('accessToken') ||
            key.includes('refreshToken')) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear session storage as well
      sessionStorage.clear();
      
      logger.debug("✅ All auth data cleared");
    } catch (error) {
      logger.error("❌ Error clearing auth data:", error);
    }
  }

  // ========== VALIDATION AND UTILITY METHODS ==========

  isPasswordChangeRequired(signInResult) {
    return (
      signInResult.nextStep?.signInStep ===
      "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
    );
  }

  handleAuthError(error) {
    const errorMap = {
      NotAuthorizedException: AUTH_ERROR_MESSAGES.INVALID_CREDENTIALS,
      UserNotConfirmedException: "Usuario no confirmado",
      UserNotFoundException: "Usuario no encontrado",
      TooManyRequestsException: AUTH_ERROR_MESSAGES.TOO_MANY_ATTEMPTS,
      InvalidPasswordException: "Contraseña inválida",
    };

    return new Error(
      errorMap[error.name] || "Error de autenticación. Intente nuevamente"
    );
  }

  validateCredentials(credentials) {
    const { username, password } = credentials;
    const { MIN_USERNAME_LENGTH, MAX_USERNAME_LENGTH, MIN_PASSWORD_LENGTH } =
      AUTH_CONFIG.VALIDATION;

    // Validación de tipos
    if (typeof username !== 'string' || typeof password !== 'string') {
      throw new Error("Credenciales inválidas");
    }

    // Sanitización básica
    const sanitizedUsername = username.trim();
    const sanitizedPassword = password.trim();

    if (
      !sanitizedUsername ||
      sanitizedUsername.length < MIN_USERNAME_LENGTH ||
      sanitizedUsername.length > MAX_USERNAME_LENGTH
    ) {
      throw new Error(
        `El nombre de usuario debe tener entre ${MIN_USERNAME_LENGTH} y ${MAX_USERNAME_LENGTH} caracteres`
      );
    }

    if (!sanitizedPassword || sanitizedPassword.length < MIN_PASSWORD_LENGTH) {
      throw new Error(
        `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres`
      );
    }

    // Prevenir inyección de caracteres especiales
    if (/[<>\"'&]/.test(sanitizedUsername)) {
      throw new Error("Nombre de usuario contiene caracteres no permitidos");
    }

    return true;
  }

  getUserRole(groups) {
    return getUserHighestRole(groups);
  }

  /**
   * Check if user is currently authenticated
   * @returns {Promise<boolean>}
   */
  async isAuthenticated() {
    try {
      const session = await fetchAuthSession();
      return !!(session.tokens && session.tokens.accessToken);
    } catch (error) {
      return false;
    }
  }
}

export default new AuthService();
